import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type { PayoutDetail, PayoutList, PayoutStatusValue, PayoutSummary, SiteList, SiteSummary } from "../../api/types";
import { RequireMfa } from "../../auth/guards";
import { CursorListView } from "../../components/CursorListView";
import {
  Banner,
  Button,
  ButtonLink,
  Card,
  Checkbox,
  EmptyState,
  Input,
  Money,
  MoneyStat,
  PageHeader,
  QueryView,
  RefId,
  SkeletonBlock,
  StatusPill,
  Table,
  type Tone,
  useToast,
} from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { newIdempotencyKey } from "../../lib/idempotency";
import { amountValidationMessage, compareDecimal, formatMoney, isZeroDecimal, subtractDecimal, validateAmountInput } from "../../lib/money";
import { useCursorList } from "../../lib/useCursorList";
import { PromotionalEarningsCard } from "./PromotionalEarnings";

// UX-DESIGN.md §3.5 (migration 0057):
//   GET  /v1/payouts/availability   available vs recognized, holds by class, in-flight claims (G-41)
//   POST /v1/payouts/request        RequireRecentMFA; typed 422 rejections (G-22)
//   GET  /v1/payouts, /v1/payouts/{id}   list and full lifecycle (G-40)
// The default request amount is the *available* figure, never the
// recognized total.

/** The customer lifecycle (payoutStatus, publisher_reads.go:575-586), stated as what happened. */
export const PAYOUT_STATUS: Record<PayoutStatusValue, { tone: Tone; label: string; detail: string }> = {
  requested: { tone: "warning", label: "Requested", detail: "Your request is recorded and the amount is set aside. It's queued for processing." },
  processing: { tone: "warning", label: "Processing", detail: "Processing with your bank — this can take a few days." },
  paid: { tone: "success", label: "Paid", detail: "The payout was sent to your bank account." },
  failed: {
    tone: "error",
    label: "Failed",
    detail: "This payout didn't go through. The amount returns to your recognized balance. Contact support if it keeps happening.",
  },
  cancelled: {
    tone: "warning",
    label: "Cancelled",
    detail:
      "Cancelled: earnings were reassigned by a debt case before this payout reached your bank. Whatever was still yours returned to your recognized balance — request a payout again for what's left.",
  },
  unknown: { tone: "warning", label: "Confirming", detail: "We're confirming what happened with this payout." },
  provisional_hold: { tone: "warning", label: "Under review", detail: "This payout is under review." },
  reversed: {
    tone: "error",
    label: "Reversed",
    detail: "This payout was paid and then reversed in full. The case below explains why.",
  },
  partially_reversed: {
    tone: "warning",
    label: "Partly reversed",
    detail: "This payout was paid and then partly reversed. The case below explains why.",
  },
};

const HOLD_CLASS: Record<string, string> = {
  safety_review: "Held pending a safety review — released only by that review, not on a date",
  active_dispute: "Held by an advertiser's open dispute of the delivery — released when the dispute is decided",
  debt_offset: "Set aside for your outstanding platform debt — the next request nets it first",
  not_claimable: "Recognized, but not traceable to a delivery a payout can claim — contact support",
  standard_hold: "Held under the standard 14-day hold from when it was recognized — released on the date shown",
};

function useSites() {
  const ep = useEndpoints();
  return useCursorList<SiteList, SiteSummary>(["sites"], (cursor) => ep.publisher.listSites(cursor), (p) => p.sites);
}

export function PayoutsPage() {
  return (
    <div className="stack-lg">
      <PageHeader title="Payouts" description="Payouts are requested for your whole account, not per site." />
      <RequireMfa>
        <RequestPayoutCard />
      </RequireMfa>
      <PromotionalEarningsCard compact />
      <PayoutHistoryCard />
    </div>
  );
}

function PayoutHistoryCard() {
  const ep = useEndpoints();
  const list = useCursorList<PayoutList, PayoutSummary>(["payouts"], (cursor) => ep.publisher.listPayouts(cursor), (p) => p.payouts);
  return (
    <Card title="Payout history">
      <CursorListView list={list} loadingLabel="Loading payouts" empty={<EmptyState icon="banknote" title="No payouts yet" />}>
        {(rows) => (
          <Table
            caption="Payouts, newest first"
            captionHidden
            rows={rows}
            rowKey={(p) => p.payout_id}
            columns={[
              { key: "at", header: "Requested", cell: (p) => <Link to={`/publisher/payouts/${p.payout_id}`}>{formatLocal(p.created_at)}</Link> },
              { key: "amt", header: "Amount", cell: (p) => <Money value={p.amount} currency={p.currency ?? "USD"} />, numeric: true },
              { key: "status", header: "Status", cell: (p) => <PayoutPill status={p.status} /> },
            ]}
          />
        )}
      </CursorListView>
    </Card>
  );
}

function PayoutPill({ status }: { status: PayoutStatusValue }) {
  const s = PAYOUT_STATUS[status] ?? { tone: "neutral" as Tone, label: "Status unavailable" };
  return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
}

function RequestPayoutCard() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const sites = useSites();
  const [onlySites, setOnlySites] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  // Availability for "specific sites" is asked per site (the endpoint's
  // site_id filter); with several selected the server still re-checks.
  const filterSite = onlySites && selected.length === 1 ? selected[0]! : null;
  const avail = useQuery({ queryKey: ["payout-availability", filterSite], queryFn: () => ep.publisher.getPayoutAvailability(filterSite) });
  const [amount, setAmount] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [intent, setIntent] = useState<{ sig: string; key: string } | null>(null);

  const m = useMutation({
    mutationFn: (v: { amount: string; siteIds: string[]; key: string; currency: string }) => ep.publisher.requestPayout(v.amount, v.siteIds, v.key),
    onSuccess: (res, variables) => {
      void qc.invalidateQueries({ queryKey: ["payouts"] });
      void qc.invalidateQueries({ queryKey: ["payout-availability"] });
      void qc.invalidateQueries({ queryKey: ["earnings"] });
      if (!isZeroDecimal(res.debt_offset)) {
        toast.show(
          <>
            <Money value={res.debt_offset} currency={variables.currency} /> of this went to your outstanding platform debt first.
          </>,
          "info",
        );
      }
      navigate(`/publisher/payouts/${res.payout_id}`);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "insufficient_claimable_balance") void avail.refetch();
    },
  });

  if (avail.isPending) return <SkeletonBlock label="Checking what's available" />;
  if (avail.isError) {
    return (
      <Card title="Request a payout">
        <QueryView query={avail}>{() => null}</QueryView>
      </Card>
    );
  }
  const a = avail.data;
  const row = a.by_currency[0];
  const currency = row?.currency ?? "USD";
  const blocked = a.payout_eligibility.blocked_reason;
  const minimum = row?.minimum_payout_amount ?? "200.0000000000";
  // Migration 0074: below the minimum, a routine request would only be
  // refused, so the button is disabled with a clear reason instead of
  // letting the publisher submit and hit the typed rejection.
  const belowMinimum = !!row && compareDecimal(row.available, minimum) < 0;
  const disabled = !!blocked || belowMinimum;
  // Default the form to the available figure (§3.5), until the user types.
  const value = amount ?? (row && !isZeroDecimal(row.available) ? trimScale(row.available) : "");

  function submit(e: FormEvent) {
    e.preventDefault();
    const v = validateAmountInput(value, currency);
    if (!v.ok) {
      setAmountError(amountValidationMessage(v.reason, currency));
      return;
    }
    if (compareDecimal(v.value, minimum) < 0) {
      setAmountError(`Payout requests are at least ${formatMoney(minimum, currency)}.`);
      return;
    }
    if (row && compareDecimal(v.value, row.available) > 0) {
      setAmountError("That's more than is available to pay out right now.");
      return;
    }
    setAmountError(null);
    const siteIds = onlySites ? selected : [];
    const sig = `${v.value}|${siteIds.slice().sort().join(",")}`;
    const key = intent?.sig === sig ? intent.key : newIdempotencyKey();
    setIntent({ sig, key });
    m.mutate({ amount: v.value, siteIds, key, currency });
  }

  return (
    <Card title="Request a payout" subtitle="You'll confirm it's you before it's submitted.">
      <div className="stack">
        {blocked ? (
          <Banner tone="warning" title="Payouts aren't enabled yet">
            <div className="stack-sm">
              <span>
                {blocked === "connect_account_missing"
                  ? "Set up your payout account to enable payouts."
                  : "Your payout account still needs information before our payment provider enables payouts."}
                {a.payout_eligibility.kyc_status !== "cleared" ? " Identity verification must also clear." : ""}
              </span>
              <span>
                <ButtonLink to="/publisher/payout-setup" size="sm" variant="primary">
                  Go to payout setup
                </ButtonLink>
              </span>
            </div>
          </Banner>
        ) : null}

        {a.by_currency.length === 0 ? <p className="muted">Nothing is recognized yet, so there's nothing to pay out.</p> : null}
        {a.by_currency.map((r) => (
          <div key={r.currency} className="stack-sm">
            <div className="grid-auto">
              <MoneyStat label="Available now" value={r.available} currency={r.currency} note="What a payout request can claim right now" />
              <MoneyStat label="Recognized" value={r.recognized} currency={r.currency} note="Before holds, in-flight claims and debt offsets" />
              {!isZeroDecimal(r.in_flight) ? (
                <MoneyStat label="In payouts already requested" value={r.in_flight} currency={r.currency} alwaysFlag />
              ) : null}
              <MoneyStat
                label="Minimum payout"
                value={r.minimum_payout_amount}
                currency={r.currency}
                note={
                  compareDecimal(r.available, r.minimum_payout_amount) >= 0
                    ? "Reached — a payout can be requested"
                    : `${formatMoney(subtractDecimal(r.minimum_payout_amount, r.available), r.currency)} more available needed`
                }
              />
            </div>
            {blocked && !isZeroDecimal(r.claimable) ? (
              <span className="small muted">
                <Money value={r.claimable} currency={r.currency} /> becomes available once payouts are enabled.
              </span>
            ) : null}
            {r.holds.length ? (
              <Table
                caption="Held back from the recognized balance"
                rows={r.holds}
                rowKey={(h) => h.class}
                columns={[
                  {
                    key: "class",
                    header: "Reason",
                    cell: (h) => (
                      <>
                        {HOLD_CLASS[h.class] ?? h.class}
                        {h.link ? (
                          <>
                            {" — "}
                            <Link to={h.link}>details</Link>
                          </>
                        ) : null}
                      </>
                    ),
                  },
                  { key: "amt", header: "Amount", cell: (h) => <Money value={h.amount} currency={r.currency} />, numeric: true },
                  { key: "when", header: "Earliest release", cell: (h) => (h.earliest_release_at ? formatLocal(h.earliest_release_at) : "Not date-based") },
                ]}
              />
            ) : null}
            {!isZeroDecimal(r.debt) ? (
              <span className="small">
                <Money value={r.debt} currency={r.currency} /> is owed to the platform (<Link to="/publisher/debt-cases">see the case</Link>).
                {r.debt_collectible && !isZeroDecimal(r.debt_collectible)
                  ? " The collectible part is netted automatically from your next payout request, oldest first."
                  : " It's under appeal, so it isn't netted from payout requests yet."}
              </span>
            ) : null}
          </div>
        ))}
        {a.encumbered.length ? (
          <Banner tone="info" title="Already claimed by payouts in progress">
            <div className="stack-sm">
              <span>This money can't be requested again until those payouts finish:</span>
              {a.encumbered.map((p) => (
                <span key={p.payout_id}>
                  <Link to={`/publisher/payouts/${p.payout_id}`}>
                    <Money value={p.amount} currency={p.currency} /> — {PAYOUT_STATUS[p.state]?.label ?? p.state}
                  </Link>
                  {!isZeroDecimal(p.provisionally_held) ? (
                    <>
                      {" "}
                      (<Money value={p.provisionally_held} currency={p.currency} /> under review)
                    </>
                  ) : null}
                </span>
              ))}
            </div>
          </Banner>
        ) : null}

        <form className="stack" onSubmit={submit} noValidate>
          <div style={{ maxWidth: 320 }}>
            <Input
              label={`Amount (${currency})`}
              inputMode="decimal"
              value={value}
              onChange={(e) => setAmount(e.target.value)}
              error={amountError}
              disabled={!!blocked}
              hint="Defaults to what's available now."
            />
          </div>
          <Checkbox
            label="Only from specific sites"
            checked={onlySites}
            onChange={(e) => setOnlySites(e.target.checked)}
            disabled={!!blocked || sites.rows.length === 0}
            hint="Defaults to all your sites."
          />
          {onlySites ? (
            <fieldset className="radio-group">
              <legend>Sites</legend>
              {sites.rows.map((s) => (
                <Checkbox
                  key={s.site_id}
                  label={s.domain}
                  checked={selected.includes(s.site_id)}
                  onChange={(e) => setSelected((xs) => (e.target.checked ? [...xs, s.site_id] : xs.filter((x) => x !== s.site_id)))}
                />
              ))}
            </fieldset>
          ) : null}
          {!blocked && belowMinimum ? (
            <Banner tone="info" title="Below the minimum payout">
              A routine payout request is at least {formatMoney(minimum, currency)}. What's available now hasn't reached that yet — it stays
              recognized and keeps counting toward it as more clears its hold.
            </Banner>
          ) : null}
          {m.error ? <PayoutError err={m.error} currency={currency} /> : null}
          <div>
            <Button type="submit" loading={m.isPending} disabled={disabled}>
              Request payout
            </Button>
          </div>
          <p className="xs muted">
            Recognized earnings clear a standard 14-day hold before they're available to pay out, and a routine request is at least{" "}
            {formatMoney(minimum, currency)}. Payout requests aren't reduced by an amount owed.
          </p>
        </form>
      </div>
    </Card>
  );
}

function trimScale(v: string): string {
  return v.includes(".") ? v.replace(/0+$/, "").replace(/\.$/, "") : v;
}

/** Typed payout rejections (publisher_commands.go:614-700, request_payout 0057). */
function PayoutError({ err, currency }: { err: Error; currency: string }) {
  const api = err instanceof ApiError ? err : null;
  const available = api?.code === "insufficient_claimable_balance" ? api.detailString("available") : undefined;
  const minimum = api?.code === "below_minimum_payout" ? api.detailString("minimum") : undefined;
  return (
    <Banner tone={api?.kind === "step_up_required" ? "neutral" : "error"} live="alert">
      <div className="stack-sm">
        <span>{err.message}</span>
        {available !== undefined ? (
          <span>
            Rejected — <Money value={available} currency={api?.detailString("currency") ?? currency} /> is available to pay out now. The figure
            above has been refreshed.
          </span>
        ) : null}
        {minimum !== undefined ? (
          <span>
            Rejected — a routine payout request is at least <Money value={minimum} currency={currency} />.
          </span>
        ) : null}
        {api?.code === "connect_account_missing" || api?.code === "connect_payouts_disabled" ? (
          <span>
            <Link to="/publisher/payout-setup">Go to payout setup</Link>
          </span>
        ) : null}
        {api?.idempotencyKey ? <RefId value={api.idempotencyKey} /> : null}
        {api?.retryable ? (
          <span className="small">
            We can't tell whether this reached the server. Submitting the same amount and sites again is safe — it's recognized as the same
            request and can't pay out twice.
          </span>
        ) : null}
      </div>
    </Banner>
  );
}

export function PayoutDetailPage() {
  const { payoutId = "" } = useParams();
  const ep = useEndpoints();
  const q = useQuery({
    queryKey: ["payout", payoutId],
    queryFn: () => ep.publisher.getPayout(payoutId),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "requested" || s === "processing" || s === "unknown" ? 30_000 : false;
    },
  });
  return (
    <div className="stack-lg">
      <PageHeader title="Payout" back={{ to: "/publisher/payouts", label: "Payouts" }} description={<code>{payoutId}</code>} />
      <QueryView query={q} loadingLabel="Loading payout">
        {(p: PayoutDetail) => <PayoutDetailView p={p} />}
      </QueryView>
    </div>
  );
}

function PayoutDetailView({ p }: { p: PayoutDetail }) {
  const s = PAYOUT_STATUS[p.status] ?? { tone: "neutral" as Tone, label: "Status unavailable", detail: "We're confirming what happened with this payout." };
  const currency = p.currency ?? "USD";
  const openHolds = p.holds.filter((h) => !h.resolved_at);
  return (
    <div className="stack-lg">
      <Card>
        <div className="stack">
          <PayoutPill status={p.status} />
          <p role="status">{s.detail}</p>
          <dl className="definition-list">
            <dt>Amount</dt>
            <dd>
              <Money value={p.amount} currency={currency} />
            </dd>
            <dt>Requested</dt>
            <dd>{formatLocal(p.created_at)}</dd>
            {!isZeroDecimal(p.reversed_amount) ? (
              <>
                <dt>Reversed</dt>
                <dd>
                  <Money value={p.reversed_amount} currency={currency} />
                </dd>
              </>
            ) : null}
            {p.provider.submission_attempted_at ? (
              <>
                <dt>Sent to our payment provider</dt>
                <dd>{formatLocal(p.provider.submission_attempted_at)}</dd>
              </>
            ) : null}
          </dl>
          {p.status === "failed" ? (
            <p className="xs muted">Our payment provider's failure reason isn't stored, so it can't be shown here.</p>
          ) : null}
        </div>
      </Card>
      {p.holds.length ? (
        <Card title="Review holds" subtitle="Part of this payout was held because a delivery it paid for is under safety review.">
          <div className="stack">
            {openHolds.length ? (
              <Banner tone="warning" title="This payout is under review">
                The held amount is released or taken back only by that review, not on a date.
              </Banner>
            ) : null}
            <Table
              caption="Holds on this payout"
              captionHidden
              rows={p.holds}
              rowKey={(h) => h.hold_id}
              columns={[
                { key: "opened", header: "Opened", cell: (h) => formatLocal(h.opened_at) },
                { key: "cov", header: "Held", cell: (h) => <Money value={h.covered_amount} currency={currency} />, numeric: true },
                { key: "rev", header: "Taken back", cell: (h) => <Money value={h.reversed_amount} currency={currency} />, numeric: true },
                { key: "res", header: "Outcome", cell: (h) => (h.resolved_at ? `${h.resolution ?? "Resolved"}, ${formatLocal(h.resolved_at)}` : "Under review") },
                {
                  key: "case",
                  header: "Case",
                  cell: (h) => (h.debt_case_id ? <Link to={`/publisher/debt-cases/${h.debt_case_id}`}>Open case</Link> : "—"),
                },
              ]}
            />
          </div>
        </Card>
      ) : null}
      {p.debt_case_ids.length ? (
        <Banner tone="error" title="This payout was later found invalid">
          <div className="stack-sm">
            <span>An amount owed to the platform was recorded against it. See the case for the evidence summary and how to contest it.</span>
            {p.debt_case_ids.map((id) => (
              <Link key={id} to={`/publisher/debt-cases/${id}`}>
                Case <code>{id.slice(0, 8)}</code>
              </Link>
            ))}
          </div>
        </Banner>
      ) : null}
      {p.transfer_reversals.length ? (
        <Card title="Transfer reversals">
          <Table
            caption="Reversals of this payout's transfer"
            captionHidden
            rows={p.transfer_reversals}
            rowKey={(r) => r.transfer_reversal_id}
            columns={[
              { key: "at", header: "Started", cell: (r) => formatLocal(r.created_at) },
              { key: "amt", header: "Amount", cell: (r) => <Money value={r.amount} currency={currency} />, numeric: true },
              { key: "state", header: "State", cell: (r) => r.state },
            ]}
          />
        </Card>
      ) : null}
      {p.site_allocations.length ? (
        <Card title="By site">
          <Table
            caption="How this payout splits across your sites"
            captionHidden
            rows={p.site_allocations}
            rowKey={(a) => a.site_id}
            columns={[
              { key: "site", header: "Site", cell: (a) => <Link to={`/publisher/sites/${a.site_id}`}><code>{a.site_id.slice(0, 8)}</code></Link> },
              { key: "amt", header: "Amount", cell: (a) => <Money value={a.amount} currency={currency} />, numeric: true },
            ]}
          />
        </Card>
      ) : null}
    </div>
  );
}
